import {
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { NumberInputPercent } from "@components/ui-kit/number-input-percent";
import { Trans, t } from "@lingui/macro";
import type { BudgetCategory } from "@src/__generated__/urql-graphql";
import { CircleTickIcon } from "@src/components/ui-kit/Icon";
import { ModalCompleteExpense } from "@src/components/widgets/Modals/ModalCompleteExpense";
import { ModalInvoices } from "@src/components/widgets/Modals/ModalInvoices";
import { currency, percent } from "@src/utils/formatters";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import React from "react";
import type { TExpenseBudgetItem } from "../../../../models";
import { useInvoiceStore } from "../../../../store";
import { AlreadyInvoiced } from "./AlreadyInvoiced";
import { ClosedItemWarning } from "./ClosedItemWarning";

interface ExpenseItemProps {
  item: TExpenseBudgetItem;
  disabled: boolean;
  budgetCategoryId: BudgetCategory["id"];
}

const ExpenseItem = ({
  item,
  disabled,
  budgetCategoryId,
}: ExpenseItemProps) => {
  const { workspaceStore } = useStore();
  const store = useInvoiceStore();

  const budgetSplitForm = store.form.expense_budget_splits.$.get(item.id);
  const isChecked = !!budgetSplitForm;

  const modalInvoices = useDisclosure();
  const modalCompleteEbi = useDisclosure();

  const handleLabelClick = () => {
    if (disabled) return;
    store.toggleCheckExpenseItem(item, !isChecked, budgetCategoryId);
  };

  return (
    <Grid
      alignItems="center"
      gap="2"
      templateColumns={{
        base: "1fr",
        md: "0.4fr repeat(3, 0.2fr)",
      }}
      px="5"
      py="6"
      bg={isChecked ? "blue.50" : "transparent"}
      borderWidth="1px"
      borderColor={isChecked ? "blue.400" : "transparent"}
      rounded="xl"
    >
      {/*
      <HStack
        px="5"
        py="6"
        bg={isChecked ? 'blue.50' : 'transparent'}
        borderWidth="1px"
        borderColor={isChecked ? 'blue.400' : 'transparent'}
        rounded="xl"
      >
    */}
      <HStack align="start" flex="1">
        <Box mr="2" pt="1">
          <Checkbox
            isChecked={isChecked}
            isDisabled={disabled}
            onChange={(e) =>
              store.toggleCheckExpenseItem(
                item,
                e.target.checked,
                budgetCategoryId,
              )
            }
          />
        </Box>
        <Stack dir="column">
          {!!item.completed_at && <ClosedItemWarning />}
          <label
            htmlFor={`budget-item-${item.id}`}
            className="text-2xl font-semibold mb-3"
            onClick={handleLabelClick}
            onKeyDown={({ key }) => {
              if (key !== "Enter") return;
              handleLabelClick();
            }}
          >
            {item.title} ({currency.formatByWorkspace(item.stats.total_budget)})
          </label>

          <Button
            justifyContent="start"
            onClick={modalInvoices.onOpen}
            variant="link"
          >
            {t`Invoiced`}: {currency.formatByWorkspace(item.stats.credit)}
          </Button>
          <ModalInvoices
            {...modalInvoices}
            expenseBudgetIdemId={item.id}
            onTransfer={store.selectedProjectsRefetch}
          />

          <label
            className="text-gray-700 mb-1"
            htmlFor={`uninvoiced-${item.id}`}
          >
            <Trans>Uninvoiced:</Trans>{" "}
            {currency.formatByWorkspace(item.stats.uninvoiced)}
          </label>

          <div className="flex items-center">
            <Button onClick={modalCompleteEbi.onOpen} variant="link">
              {t`Expenses: ${currency.formatByWorkspace(item.stats.real_expenses)}`}
            </Button>
            {!!item.completed_at && (
              <div className="ml-3">
                <CircleTickIcon />
              </div>
            )}
          </div>

          <ModalCompleteExpense
            id={item.id}
            {...modalCompleteEbi}
            onAction={() => store.selectedProjectsRefetch?.()}
          />
        </Stack>
      </HStack>

      {isChecked &&
        (budgetSplitForm ? (
          <React.Fragment>
            <Stack dir="column">
              <label
                className="text-2xl text-gray-700 mb-2"
                htmlFor={`expenses-${item.id}`}
              >
                <Trans>Expenses</Trans>
              </label>

              <label
                className="text-2xl font-semibold"
                htmlFor={`expenses-${item.id}`}
              >
                {currency.formatByWorkspace(item.stats.planned_expenses)}
              </label>
            </Stack>
            <Stack dir="column">
              <label
                className="text-2xl text-gray-700 mb-2"
                htmlFor={`commission-${item.id}`}
              >
                <Trans>Commission</Trans>
              </label>
              <label
                className="text-2xl font-semibold"
                htmlFor={`commission-${item.id}`}
              >
                {currency.formatByWorkspace(item.stats.planned_commission)} (
                {percent.format(
                  item.stats.planned_commission_relative_computed,
                )}
                )
              </label>
              {!!item.completed_at && (
                <React.Fragment>
                  <label
                    className="text-2xl text-gray-700 mt-3 mb-2"
                    htmlFor={`final-commission-${item.id}`}
                  >
                    <Trans>Final commission</Trans>
                  </label>

                  <label
                    className="text-2xl font-semibold"
                    htmlFor={`final-commission-${item.id}`}
                  >
                    {currency.formatByWorkspace(item.stats.real_commission)} (
                    {percent.format(item.stats.real_commission_relative)})
                  </label>
                </React.Fragment>
              )}
            </Stack>
          </React.Fragment>
        ) : (
          <div className="w-2/5" />
        ))}

      <Stack dir="column">
        {isChecked &&
          (budgetSplitForm ? (
            <React.Fragment>
              <label
                className="text-2xl text-gray-700 mb-2"
                htmlFor={`amount-${item.id}`}
              >
                <Trans>Amount</Trans>
              </label>

              <NumberInputPercent
                currency
                placeholder={t`Amount`}
                disabled={disabled}
                suffix={workspaceStore.settings!.currency.code}
                {...fieldNumberModelToInputProps(budgetSplitForm.$.amount)}
                onChange={(value) =>
                  NumberModel.setFieldState(budgetSplitForm.$.amount, value)
                }
                percentProps={{
                  ...fieldNumberModelToInputProps(
                    budgetSplitForm.$.amount_in_percent,
                  ),
                  isDisabled:
                    !store.isRFI && store.isEdit && item.stats.credit > 0,
                  disabledTooltip: t`Percentage editing is disabled for existing invoices. The uninvoiced amount may have changed since this invoice was created.`,
                  onChange: (value: string) =>
                    NumberModel.setFieldState(
                      budgetSplitForm.$.amount_in_percent,
                      value,
                    ),
                  availableValue: item.stats.uninvoiced,
                }}
              />
            </React.Fragment>
          ) : (
            <AlreadyInvoiced />
          ))}
      </Stack>
    </Grid>
  );
};

export default observer(ExpenseItem);
